import React from 'react';
import { 
  Typography, 
  Grid, 
  Paper, 
  Button, 
  Box, 
  Container,
  alpha 
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import SchoolIcon from '@mui/icons-material/School';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

// Paleta profesional estilo LinkedIn Learning / Coursera
const COLORS = {
  gradient1: '#00b894',      // Verde esmeralda (crecimiento, aprendizaje)
  gradient2: '#0984e3',      // Azul brillante (confianza, profesionalismo)
  primary: '#0984e3',        // Azul principal
  success: '#00b894',        // Verde éxito
  warning: '#eb9800ff',        // Amarillo cálido
  error: '#d63031',          // Rojo error
  info: '#74b9ff',           // Azul claro
  purple: '#a29bfe',         // Violeta suave (creatividad)
  orange: '#e17055',         // Naranja coral
  dark: '#2d3436',           // Gris oscuro texto
  light: '#dfe6e9'           // Gris claro fondos
};

// Colores únicos para cada card
const CARD_THEMES = {
  courses: { color: COLORS.primary, icon: '📚' },
  supervisor: { color: '#6c5ce7', icon: '👥' },
  generator: { color: COLORS.success, icon: '🤖' },
  admin: { color: COLORS.orange, icon: '⚙️' },
  create: { color: '#00cec9', icon: '✨' }
};

const FeatureCard = ({ title, description, icon, linkTo, theme }) => (
  <Paper 
    elevation={0}
    sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      borderRadius: 4,
      border: '2px solid',
      borderColor: alpha(theme.color, 0.15),
      background: `linear-gradient(145deg, ${alpha(theme.color, 0.03)} 0%, #ffffff 100%)`,
      overflow: 'hidden',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      position: 'relative',
      '&:hover': {
        transform: 'translateY(-8px)',
        boxShadow: `0 20px 40px ${alpha(theme.color, 0.2)}`,
        borderColor: alpha(theme.color, 0.4),
        '& .card-icon-box': {
          transform: 'scale(1.1) rotate(5deg)',
        },
        '& .card-emoji': {
          transform: 'scale(1.2)',
        },
        '& .card-arrow': {
          transform: 'translateX(4px)',
          opacity: 1
        }
      }
    }}
  >
    {/* Decorative top bar */}
    <Box 
      sx={{ 
        height: 4, 
        background: `linear-gradient(90deg, ${theme.color} 0%, ${alpha(theme.color, 0.5)} 100%)` 
      }} 
    />
    
    <Box sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
      {/* Icon row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Box
          className="card-icon-box"
          sx={{
            width: 56,
            height: 56,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.color} 0%, ${alpha(theme.color, 0.7)} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 8px 24px ${alpha(theme.color, 0.3)}`,
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            color: 'white'
          }}
        >
          {React.cloneElement(icon, { sx: { fontSize: 28 } })}
        </Box>
        <Typography 
          className="card-emoji"
          sx={{ 
            fontSize: '2rem',
            transition: 'all 0.3s ease'
          }}
        >
          {theme.icon}
        </Typography>
      </Box>
      
      {/* Title */}
      <Typography 
        variant="h6" 
        sx={{ 
          fontWeight: 800,
          color: COLORS.dark,
          mb: 1.5,
          fontSize: '1.1rem',
          letterSpacing: -0.3
        }}
      >
        {title}
      </Typography>
      
      {/* Description */}
      <Typography 
        variant="body2" 
        sx={{ 
          color: '#636e72',
          flexGrow: 1,
          lineHeight: 1.6,
          fontSize: '0.9rem'
        }}
      >
        {description}
      </Typography>
      
      {/* Button */}
      <Button 
        component={RouterLink} 
        to={linkTo} 
        variant="contained"
        disableElevation
        endIcon={
          <Box 
            className="card-arrow"
            component="span"
            sx={{ 
              transition: 'all 0.3s ease',
              opacity: 0.7,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            →
          </Box>
        }
        sx={{ 
          mt: 3,
          py: 1.5,
          px: 3,
          borderRadius: 2.5,
          textTransform: 'none',
          fontWeight: 700,
          fontSize: '0.9rem',
          background: `linear-gradient(135deg, ${theme.color} 0%, ${alpha(theme.color, 0.85)} 100%)`,
          boxShadow: `0 4px 14px ${alpha(theme.color, 0.35)}`,
          '&:hover': {
            background: `linear-gradient(135deg, ${theme.color} 0%, ${alpha(theme.color, 0.9)} 100%)`,
            boxShadow: `0 6px 20px ${alpha(theme.color, 0.45)}`,
          }
        }}
      >
        Acceder
      </Button>
    </Box>
  </Paper>
);

const StatCard = ({ icon, value, label, color }) => (
  <Box 
    sx={{ 
      textAlign: 'center',
      p: 2
    }}
  >
    <Box 
      sx={{ 
        width: 48,
        height: 48,
        borderRadius: 2,
        backgroundColor: alpha(color, 0.15),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mx: 'auto',
        mb: 1
      }}
    >
      {React.cloneElement(icon, { sx: { color: color, fontSize: 24 } })}
    </Box>
    <Typography variant="h5" sx={{ fontWeight: 800, color: COLORS.dark }}>
      {value}
    </Typography>
    <Typography variant="body2" sx={{ color: '#636e72', fontWeight: 500 }}>
      {label}
    </Typography>
  </Box>
);

const Home = () => {
  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f8fffe 0%, #f0f7ff 100%)'
      }}
    >
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Hero Header */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
            borderRadius: 4,
            p: { xs: 3, md: 5 },
            mb: 4,
            position: 'relative',
            overflow: 'hidden',
            boxShadow: `0 20px 60px ${alpha(COLORS.gradient1, 0.3)}`
          }}
        >
          {/* Decorative circles */}
          <Box
            sx={{
              position: 'absolute',
              top: -50,
              right: -50,
              width: 200,
              height: 200,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.1)',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -30,
              left: '30%',
              width: 120,
              height: 120,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.08)',
            }}
          />
          
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: 3,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <SchoolIcon sx={{ fontSize: 36, color: 'white' }} />
              </Box>
              <Box>
                <Typography 
                  variant="h3" 
                  sx={{ 
                    color: 'white',
                    fontWeight: 900,
                    letterSpacing: -1,
                    lineHeight: 1.1,
                    fontSize: { xs: '1.8rem', md: '2.5rem' }
                  }}
                >
                  Aula Digital Industrial
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: 'rgba(255,255,255,0.85)',
                    fontWeight: 500,
                    mt: 0.5
                  }}
                >
                  Plataforma de Formación y Capacitación
                </Typography>
              </Box>
            </Box>
            
            <Typography 
              sx={{ 
                color: 'rgba(255,255,255,0.9)',
                maxWidth: 600,
                lineHeight: 1.7,
                fontSize: '1rem',
                mt: 2
              }}
            >
              Gestiona cursos, supervisa el progreso de tu equipo y potencia 
              el desarrollo profesional con nuestra plataforma de aprendizaje.
            </Typography>

            {/* Quick stats */}
            <Paper
              elevation={0}
              sx={{
                mt: 3,
                p: 1,
                borderRadius: 3,
                backgroundColor: 'rgba(255,255,255,0.95)',
                backdropFilter: 'blur(10px)',
                display: 'inline-flex',
                gap: 2
              }}
            >
              <StatCard 
                icon={<PlayCircleFilledIcon />} 
                value="24" 
                label="Cursos" 
                color={COLORS.primary}
              />
              <StatCard 
                icon={<TrendingUpIcon />} 
                value="89%" 
                label="Completados" 
                color={COLORS.success}
              />
              <StatCard 
                icon={<EmojiEventsIcon />} 
                value="156" 
                label="Certificados" 
                color={COLORS.warning}
              />
            </Paper>
          </Box>
        </Box>

        {/* Section Title */}
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 800, 
              color: COLORS.dark,
              mb: 1
            }}
          >
            Accesos Rápidos
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: '#636e72'
            }}
          >
            Selecciona una opción para comenzar
          </Typography>
        </Box>

        {/* Feature Cards Grid */}
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <FeatureCard
              title="Gestionar Cursos"
              description="Visualiza, edita y organiza todos los cursos disponibles en la plataforma."
              icon={<ListAltIcon />}
              linkTo="/courses"
              theme={CARD_THEMES.courses}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FeatureCard
              title="Panel de Supervisor"
              description="Supervisa el progreso de los empleados, asigna cursos y realiza seguimiento mediante notificaciones."
              icon={<SupervisorAccountIcon />}
              linkTo="/supervisor-dashboard"
              theme={CARD_THEMES.supervisor}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FeatureCard
              title="Generador de Cursos IA"
              description="Crea cursos automáticamente a partir de documentos PDF usando inteligencia artificial."
              icon={<AutoStoriesIcon />}
              linkTo="/auto-course-generator"
              theme={CARD_THEMES.generator}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FeatureCard
              title="Gestión de Usuarios"
              description="Registro, edición y administración de usuarios del sistema de formación."
              icon={<AdminPanelSettingsIcon />}
              linkTo="/admin-dashboard"
              theme={CARD_THEMES.admin}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FeatureCard
              title="Crear Nuevo Curso"
              description="Diseña y publica un nuevo curso de forma manual con contenido personalizado."
              icon={<AddCircleOutlineIcon />}
              linkTo="/create-course"
              theme={CARD_THEMES.create}
            />
          </Grid>
        </Grid>

        {/* Footer info */}
        <Box 
          sx={{ 
            mt: 6, 
            pt: 3, 
            borderTop: `2px solid ${alpha(COLORS.gradient1, 0.1)}`,
            textAlign: 'center'
          }}
        >
          <Typography variant="body2" sx={{ color: '#b2bec3' }}>
            © 2025 Aula Digital Industrial — Plataforma de Formación Corporativa
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Home;